import React from 'react';
import { PiPlus, PiTrash } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import {
  FieldInputProps,
  WorkflowFieldCardProps,
  WorkflowInputField,
  WorkflowSubFieldCardProps,
} from './workflow-input-editor.types';

const EnumValuesEditor: React.FC<{
  fieldId: string;
  values?: string[];
  draft: string;
  onDraftChange: (value: string) => void;
  onAdd: () => void;
  onRemove: (value: string) => void;
}> = ({ fieldId, values, draft, onDraftChange, onAdd, onRemove }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs text-slate-600" htmlFor={`enum-${fieldId}`}>
      Valores permitidos (opcional)
    </label>
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {(values || []).map((val) => (
          <span
            key={`${fieldId}-${val}`}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 text-xs px-2 py-1"
          >
            {val}
            <button
              type="button"
              className="text-slate-500 hover:text-rose-600"
              onClick={() => onRemove(val)}
              aria-label={`Eliminar valor ${val}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
        <div className="flex-1 flex flex-col gap-1">
          <input
            className="border rounded px-2 py-1.5 text-sm w-full"
            id={`enum-${fieldId}`}
            name={`enum-${fieldId}`}
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAdd();
              }
            }}
            placeholder="ej: open"
          />
        </div>
        <Button type="button" variant="primary" size="sm" onClick={onAdd}>
          <div className="flex items-center gap-2">
            <PiPlus className="w-4 h-4" />
            <span>Agregar</span>
          </div>
        </Button>
      </div>
      <Text size="tiny" className="text-slate-500">
        Presiona Enter o el botón para agregar. No se permiten duplicados ni
        valores vacíos.
      </Text>
    </div>
  </div>
);

const FieldInputs: React.FC<FieldInputProps> = ({
  field,
  enumDraft,
  onEnumDraftChange,
  onAddEnumValue,
  onRemoveEnumValue,
  onUpdate,
  showAllowAdditional = true,
  allowObjectType = true,
}) => {
  const renderStringFields = (current: WorkflowInputField) => (
    <>
      <div className="flex flex-col gap-1">
        <label
          className="text-xs text-slate-600"
          htmlFor={`format-${current.id}`}
        >
          Formato
        </label>
        <select
          className="border rounded px-2 py-1.5 text-sm w-full"
          id={`format-${current.id}`}
          name={`format-${current.id}`}
          value={current.format || ''}
          onChange={(e) =>
            onUpdate({
              format: e.target.value || undefined,
            })
          }
        >
          <option value="">Sin formato</option>
          <option value="email">Email</option>
          <option value="date-time">Fecha y hora</option>
          <option value="uri">URL</option>
        </select>
      </div>

      <EnumValuesEditor
        fieldId={current.id}
        values={current.enumValues}
        draft={enumDraft}
        onDraftChange={onEnumDraftChange}
        onAdd={onAddEnumValue}
        onRemove={onRemoveEnumValue}
      />

      <div className="flex flex-col gap-1">
        <label
          className="text-xs text-slate-600"
          htmlFor={`minlen-${current.id}`}
        >
          Longitud mínima
        </label>
        <input
          type="number"
          className="border rounded px-2 py-1.5 text-sm w-full"
          id={`minlen-${current.id}`}
          name={`minlen-${current.id}`}
          value={
            typeof current.minLength === 'number'
              ? String(current.minLength)
              : ''
          }
          onChange={(e) =>
            onUpdate({
              minLength:
                e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
          min={0}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          className="text-xs text-slate-600"
          htmlFor={`maxlen-${current.id}`}
        >
          Longitud máxima
        </label>
        <input
          type="number"
          className="border rounded px-2 py-1.5 text-sm w-full"
          id={`maxlen-${current.id}`}
          name={`maxlen-${current.id}`}
          value={
            typeof current.maxLength === 'number'
              ? String(current.maxLength)
              : ''
          }
          onChange={(e) =>
            onUpdate({
              maxLength:
                e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
          min={0}
        />
      </div>
    </>
  );

  const renderNumberFields = (current: WorkflowInputField) => (
    <>
      <div className="flex flex-col gap-1">
        <label className="text-xs text-slate-600" htmlFor={`min-${current.id}`}>
          Mínimo
        </label>
        <input
          type="number"
          className="border rounded px-2 py-1.5 text-sm w-full"
          id={`min-${current.id}`}
          name={`min-${current.id}`}
          value={typeof current.min === 'number' ? String(current.min) : ''}
          onChange={(e) =>
            onUpdate({
              min: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs text-slate-600" htmlFor={`max-${current.id}`}>
          Máximo
        </label>
        <input
          type="number"
          className="border rounded px-2 py-1.5 text-sm w-full"
          id={`max-${current.id}`}
          name={`max-${current.id}`}
          value={typeof current.max === 'number' ? String(current.max) : ''}
          onChange={(e) =>
            onUpdate({
              max: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
        />
      </div>
    </>
  );

  const renderArrayFields = (current: WorkflowInputField) => (
    <>
      <div className="flex flex-col gap-1">
        <label
          className="text-xs text-slate-600"
          htmlFor={`itemtype-${current.id}`}
        >
          Tipo de ítem
        </label>
        <select
          className="border rounded px-2 py-1.5 text-sm w-full"
          id={`itemtype-${current.id}`}
          name={`itemtype-${current.id}`}
          value={current.itemType || 'string'}
          onChange={(e) =>
            onUpdate({
              itemType: e.target.value as WorkflowInputField['itemType'],
            })
          }
        >
          <option value="string">Texto</option>
          <option value="number">Número</option>
          <option value="integer">Entero</option>
          <option value="boolean">Booleano</option>
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label
          className="text-xs text-slate-600"
          htmlFor={`minitems-${current.id}`}
        >
          Mínimo de ítems
        </label>
        <input
          type="number"
          className="border rounded px-2 py-1.5 text-sm w-full"
          id={`minitems-${current.id}`}
          name={`minitems-${current.id}`}
          value={
            typeof current.minItems === 'number' ? String(current.minItems) : ''
          }
          onChange={(e) =>
            onUpdate({
              minItems:
                e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
          min={0}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          className="text-xs text-slate-600"
          htmlFor={`maxitems-${current.id}`}
        >
          Máximo de ítems
        </label>
        <input
          type="number"
          className="border rounded px-2 py-1.5 text-sm w-full"
          id={`maxitems-${current.id}`}
          name={`maxitems-${current.id}`}
          value={
            typeof current.maxItems === 'number' ? String(current.maxItems) : ''
          }
          onChange={(e) =>
            onUpdate({
              maxItems:
                e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
          min={0}
        />
      </div>
    </>
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="flex flex-col gap-1">
        <label className="text-xs text-slate-600" htmlFor={`name-${field.id}`}>
          Nombre
        </label>
        <input
          className="border rounded px-2 py-1.5 text-sm w-full"
          id={`name-${field.id}`}
          name={`name-${field.id}`}
          value={field.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder="ej: conversationId"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs text-slate-600" htmlFor={`type-${field.id}`}>
          Tipo
        </label>
        <select
          className="border rounded px-2 py-1.5 text-sm w-full"
          id={`type-${field.id}`}
          name={`type-${field.id}`}
          value={field.type}
          onChange={(e) =>
            onUpdate({
              type: e.target.value as WorkflowInputField['type'],
              itemType:
                e.target.value === 'array'
                  ? field.itemType || 'string'
                  : undefined,
            })
          }
        >
          <option value="string">Texto</option>
          <option value="number">Número</option>
          <option value="integer">Entero</option>
          <option value="boolean">Booleano</option>
          {allowObjectType && <option value="object">Objeto</option>}
          <option value="array">Arreglo</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <input
          id={`required-${field.id}`}
          type="checkbox"
          className="h-4 w-4"
          checked={field.required}
          onChange={(e) => onUpdate({ required: e.target.checked })}
        />
        <label
          htmlFor={`required-${field.id}`}
          className="text-sm text-slate-700"
        >
          Requerido
        </label>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs text-slate-600" htmlFor={`desc-${field.id}`}>
          Descripción
        </label>
        <input
          className="border rounded px-2 py-1.5 text-sm w-full"
          id={`desc-${field.id}`}
          name={`desc-${field.id}`}
          value={field.description || ''}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="Breve ayuda para el usuario"
        />
      </div>

      {field.type === 'string' && renderStringFields(field)}
      {(field.type === 'number' || field.type === 'integer') &&
        renderNumberFields(field)}
      {field.type === 'object' && showAllowAdditional && (
        <div className="flex items-center gap-2">
          <input
            id={`allow-additional-${field.id}`}
            type="checkbox"
            className="h-4 w-4"
            checked={field.allowAdditionalProps ?? false}
            onChange={(e) =>
              onUpdate({
                allowAdditionalProps: e.target.checked,
              })
            }
          />
          <label
            htmlFor={`allow-additional-${field.id}`}
            className="text-sm text-slate-700"
          >
            Permitir propiedades adicionales
          </label>
        </div>
      )}
      {field.type === 'array' && renderArrayFields(field)}
    </div>
  );
};

export const WorkflowSubFieldCard: React.FC<WorkflowSubFieldCardProps> = ({
  field,
  enumDrafts,
  fieldErrorsMap,
  onEnumDraftChange,
  onAddEnumValue,
  onRemoveEnumValue,
  onUpdateField,
  onRemoveField,
}) => {
  const draft = enumDrafts[field.id] || '';
  const currentErrors = fieldErrorsMap[field.id] || [];

  return (
    <div className="w-full border rounded bg-slate-50 shadow-sm p-3 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col">
          <Text weight="medium" className="text-slate-900">
            {field.name || 'Nuevo subcampo'}
          </Text>
          <Text size="tiny" className="text-slate-500">
            Configura subcampo del objeto.
          </Text>
        </div>
        <Button
          variant="neutral"
          appearance="ghost"
          size="sm"
          isIconOnly
          onClick={onRemoveField}
          aria-label="Eliminar subcampo"
        >
          <PiTrash className="w-4 h-4" />
        </Button>
      </div>

      <FieldInputs
        field={field}
        enumDraft={draft}
        onEnumDraftChange={(val) => onEnumDraftChange(field.id, val)}
        onAddEnumValue={() => onAddEnumValue(field.id)}
        onRemoveEnumValue={(val) => onRemoveEnumValue(field.id, val)}
        onUpdate={onUpdateField}
        showAllowAdditional={false}
        allowObjectType={false}
      />

      {currentErrors.length > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2 space-y-1">
          {currentErrors.map((err, idx) => (
            <div key={`${field.id}-err-${idx}`}>{err}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export const WorkflowFieldCard: React.FC<WorkflowFieldCardProps> = ({
  field,
  enumDrafts,
  fieldErrorsMap,
  onEnumDraftChange,
  onAddEnumValue,
  onRemoveEnumValue,
  onUpdateField,
  onRemoveField,
  onAddSubField,
  onUpdateSubField,
  onRemoveSubField,
}) => {
  const draft = enumDrafts[field.id] || '';
  const currentErrors = fieldErrorsMap[field.id] || [];

  return (
    <div className="border rounded bg-white shadow-sm p-3 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col">
          <Text weight="medium" className="text-slate-900">
            {field.name || 'Nuevo campo'}
          </Text>
          <Text size="tiny" className="text-slate-500">
            Configura nombre, tipo y restricciones.
          </Text>
        </div>
        <Button
          variant="neutral"
          appearance="ghost"
          size="sm"
          isIconOnly
          onClick={onRemoveField}
          aria-label="Eliminar campo"
        >
          <PiTrash className="w-4 h-4" />
        </Button>
      </div>

      <FieldInputs
        field={field}
        enumDraft={draft}
        onEnumDraftChange={(val) => onEnumDraftChange(field.id, val)}
        onAddEnumValue={() => onAddEnumValue(field.id)}
        onRemoveEnumValue={(val) => onRemoveEnumValue(field.id, val)}
        onUpdate={onUpdateField}
      />

      {field.type === 'object' && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Text size="tiny" weight="medium" className="text-slate-600">
              Subcampos
            </Text>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">
                {(field.subFields || []).length}/20 subcampos
              </span>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={onAddSubField}
              >
                <div className="flex items-center gap-2">
                  <PiPlus className="w-4 h-4" />
                  <span>Agregar subcampo</span>
                </div>
              </Button>
            </div>
          </div>
          <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
            {(field.subFields || []).length === 0 ? (
              <EmptyState
                description="Agrega subcampos para este objeto."
                appearance="soft"
                variant="neutral"
                size="sm"
                className="py-4"
              />
            ) : (
              field.subFields!.map((sf) => (
                <WorkflowSubFieldCard
                  key={sf.id}
                  field={sf}
                  enumDrafts={enumDrafts}
                  fieldErrorsMap={fieldErrorsMap}
                  onEnumDraftChange={onEnumDraftChange}
                  onAddEnumValue={onAddEnumValue}
                  onRemoveEnumValue={onRemoveEnumValue}
                  onUpdateField={(patch) => onUpdateSubField(sf.id, patch)}
                  onRemoveField={() => onRemoveSubField(sf.id)}
                />
              ))
            )}
          </div>
        </div>
      )}

      {currentErrors.length > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2 space-y-1">
          {currentErrors.map((err, idx) => (
            <div key={`${field.id}-err-${idx}`}>{err}</div>
          ))}
        </div>
      )}
    </div>
  );
};
